import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { Banner, Card, Skeleton } from '../../components/ui.tsx'
import { asAuthError, get, post } from '../../lib/api.ts'
import { useSession } from '../../lib/session.tsx'
import { AuthCard, AuthHeading, ConsentBlock, NO_CONSENTS, allConsented, type Consents } from './AuthCard.tsx'
import type { AuthError, PendingGoogleSignup, SessionUser } from '../../../shared/auth.ts'

/**
 * The last step of a first Google sign-in.
 *
 * Google has proved who someone is. It has not told us they are 18, that they accept the
 * terms, or that they know they can lose money — and this product cannot open an account
 * without all three. So a new Google identity lands here rather than in the app, and the
 * three agreements are taken exactly as the password form takes them.
 *
 * Nothing exists yet at this point: no account, no limits, no session. Closing the tab
 * here leaves no trace beyond a token that expires on its own.
 */
export function GoogleSignup() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { setUser } = useSession()

  const token = params.get('token') ?? ''
  const next = params.get('next')

  const [pending, setPending] = useState<PendingGoogleSignup | null>(null)
  const [consents, setConsents] = useState<Consents>(NO_CONSENTS)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<AuthError | null>(null)

  useEffect(() => {
    if (!token) {
      setErr({ error: 'This Google sign-in has expired. Please start again.', kind: 'invalid_token' })
      return
    }
    get<PendingGoogleSignup>(`/auth/google/pending/${encodeURIComponent(token)}`)
      .then(setPending)
      .catch((error) => setErr(asAuthError(error)))
  }, [token])

  const submit = async () => {
    setBusy(true)
    setErr(null)
    try {
      const { user } = await post<{ user: SessionUser }>('/auth/google/complete', {
        token,
        ...consents,
      })
      setUser(user)
      navigate(next && next.startsWith('/') ? next : '/', { replace: true })
    } catch (error) {
      setErr(asAuthError(error))
    } finally {
      setBusy(false)
    }
  }

  // An expired or already-used token cannot be recovered from this screen — the only way
  // forward is another handshake, so that is the only thing offered.
  if (err?.kind === 'invalid_token' || err?.kind === 'email_taken') {
    return (
      <AuthCard width={440}>
        <AuthHeading title="Finish signing up" />
        <Banner tone="flatten" icon="✕" title="This sign-in has expired">
          {err.error}
        </Banner>
        <Link className="btn primary block lg" to="/signup" style={{ marginTop: 16 }}>
          Back to sign-up
        </Link>
      </AuthCard>
    )
  }

  return (
    <AuthCard width={440}>
      <AuthHeading title="One last thing" sub="Starts in paper mode. No card required." />

      {err && !err.kind && (
        <Banner tone="flatten" icon="✕" title="Could not create the account">
          {err.error}
        </Banner>
      )}

      <Card pad>
        {!pending ? (
          <Skeleton rows={3} />
        ) : (
          <>
            <p className="u-sm u-muted" style={{ marginBottom: 4 }}>
              Signing up as
            </p>
            <p className="mono" style={{ marginBottom: 16 }}>
              {pending.email}
            </p>

            <hr className="hr" />

            <ConsentBlock value={consents} onChange={setConsents} />

            <button
              type="button"
              className="btn primary block lg"
              style={{ marginTop: 18 }}
              disabled={!allConsented(consents) || busy}
              onClick={submit}
            >
              {busy ? 'Creating account…' : 'Create free account'}
            </button>

            {!allConsented(consents) && (
              <p className="u-center u-sm u-faint" style={{ marginTop: 8 }}>
                Confirm all three above to continue.
              </p>
            )}

            <p className="u-center u-sm u-faint" style={{ marginTop: 12 }}>
              Not you? <Link to="/login">Sign in as someone else</Link>
            </p>
          </>
        )}
      </Card>

      <p className="u-center u-faint u-sm" style={{ marginTop: 18 }}>
        KellyStake never holds your funds. The only thing we charge is
        the subscription — and paper mode is free.
      </p>
    </AuthCard>
  )
}
